<!-- eslint-disable vue/no-v-html -->
<template>
  <BCard>
    <div class="d-flex align-items-center">
      <button
        class="back-btn"
        @click="$router.back()"
      >
        <feather-icon
          icon="ChevronLeftIcon"
          size="22"
        />
      </button>
      <span class="text-[24px] font-semibold my-auto text-black">
        {{ (isEditMode && !isDraft) ? 'Detail Update Log' : 'Buat Update Log' }}
      </span>
    </div>

    <BOverlay
      variant="light"
      :show="isLoading"
      spinner-variant="primary"
      blur="0"
      opacity=".5"
      rounded="sm"
    >

      <BRow class="mt-3">
        <!-- Left: Form -->
        <BCol md="6">
          <h5 class="text-black font-bold mb-2">
            Tambah Log Baru
          </h5>

          <!-- Judul -->
          <BFormGroup>
            <label class="text-black font-medium">Judul</label>
            <BFormInput
              v-model="form.title"
              placeholder="Masukkan judul notifikasi"
              maxlength="100"
            />
          </BFormGroup>

          <!-- Deskripsi -->
          <BFormGroup>
            <label class="text-black font-medium">Deskripsi</label>
            <quill-editor
              v-model="form.description"
              :options="editorOptions"
            />
          </BFormGroup>

          <!-- Tanggal -->
          <BFormGroup>
            <label class="text-black font-medium">Tanggal</label>
            <flat-pickr
              v-model="form.date"
              class="form-control"
              placeholder="Pilih tanggal"
              :config="dateConfig"
            />
          </BFormGroup>
        </BCol>

        <!-- Right: Preview -->
        <BCol md="6">
          <h5 class="text-black font-bold mb-2">
            Preview
          </h5>
          <div
            v-if="hasPreviewContent"
            class="rounded-lg p-3"
          >
            <div class="d-flex align-items-start gap-[12px]">
              <div class="flex-shrink-0">
                <div class="w-[50px] h-[50px] bg-[#F8F8F8] rounded-full d-flex align-items-center justify-content-center">
                  <img
                    src="https://storage.googleapis.com/komerce/rajaongkir/Icon-RajaOngkir.svg"
                    alt="icon"
                    width="25"
                    height="25"
                  >
                </div>
              </div>
              <div class="flex-grow-1 min-w-0">
                <div class="flex items-center justify-between gap-2 mb-[8px]">
                  <span class="text-black font-semibold text-truncate">
                    {{ form.title }}
                  </span>
                  <span
                    v-if="form.date"
                    class="text-[#828282] text-[12px] flex-shrink-0"
                  >
                    {{ formatPreviewDate(form.date) }}
                  </span>
                </div>
                <div
                  class="text-[#828282] text-[13px] mb-0 ql-editor !p-0"
                  v-html="form.description"
                />
              </div>
            </div>
          </div>
          <div
            v-else
            class="bg-[#F8F8F8] rounded-lg d-flex align-items-center justify-content-center"
            style="min-height: 200px;"
          >
            <div class="text-center text-[#C2C2C2]">
              <feather-icon
                icon="ImageIcon"
                size="40"
              />
            </div>
          </div>
        </BCol>
      </BRow>

      <!-- Footer Actions -->
      <div class="d-flex justify-content-end align-items-center gap-[12px] mt-3">
        <span
          class="text-black cursor-pointer"
          @click="handleDelete"
        >
          Hapus
        </span>
        <BButton
          v-if="!isEditMode || isDraft"
          variant="flat-dark"
          class="draft-btn"
          :disabled="!isFormValid"
          @click="handleDraft"
        >
          Draft
        </BButton>
        <BButton
          variant="primary"
          class="rounded-lg"
          :disabled="!isFormValid"
          @click="showConfirmModal"
        >
          {{ (isEditMode && !isDraft) ? 'Simpan Perubahan' : 'Publish' }}
        </BButton>
      </div>
    </BOverlay>

    <ModalConfirm
      :title="modalConfig.title"
      :description="modalConfig.description"
      :confirm-text="modalConfig.confirmText"
      :loading="isSubmitting"
      @confirm="handleConfirm"
    />
  </BCard>
</template>

<script>
import moment from 'moment'
import flatPickr from 'vue-flatpickr-component'
import { openApiAxiosIns } from '@/libs/axios'
import { quillEditor } from 'vue-quill-editor'
import ModalConfirm from './ModalConfirm.vue'

const BASE_URL = '/user/api/v1/admin/system-update-log'
const LIST_ROUTE = '/openapi/system-update-log'

export default {
  components: {
    flatPickr,
    ModalConfirm,
    quillEditor,
  },
  data() {
    return {
      form: {
        title: '',
        description: '',
        date: '',
      },
      status: '',
      isSubmitting: false,
      isLoading: false,
      editorOptions: {
        placeholder: 'Masukkan deskripsi',
        direction: 'ltr',
        modules: {
          toolbar: {
            container: [['bold', 'italic', 'underline']],
          },
        },
      },
      dateConfig: {
        altInput: true,
        altFormat: 'j F Y',
        dateFormat: 'Y-m-d',
        locale: {
          months: {
            shorthand: ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'],
            longhand: ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'],
          },
          weekdays: {
            shorthand: ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'],
            longhand: ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'],
          },
          firstDayOfWeek: 1,
        },
      },
    }
  },
  computed: {
    isEditMode() {
      return !!this.$route.params.id
    },
    isDraft() {
      return this.status === 'draft'
    },
    detailUrl() {
      return `${BASE_URL}/${this.$route.params.id}`
    },
    isFormValid() {
      const isDescEmpty = !this.form.description || this.form.description.replace(/<[^>]*>/g, '').trim() === ''
      return this.form.title.trim() !== ''
        && !isDescEmpty
        && this.form.date !== ''
    },
    hasPreviewContent() {
      const isDescEmpty = !this.form.description || this.form.description.replace(/<[^>]*>/g, '').trim() === ''
      return this.form.title.trim() !== '' || !isDescEmpty
    },
    modalConfig() {
      if (this.isEditMode && !this.isDraft) {
        return {
          title: 'Simpan Perubahan?',
          description: 'Perubahan yang disimpan akan memperbarui update log yang sudah ada',
          confirmText: 'Simpan Perubahan',
        }
      }
      return {
        title: 'Publish Update Log?',
        description: 'Setelah dipublikasikan, update log akan tersedia untuk seluruh pengguna',
        confirmText: 'Lanjut Publish',
      }
    },
  },
  mounted() {
    if (this.isEditMode) {
      this.getDetail()
    }
  },
  methods: {
    formatPreviewDate(date) {
      return moment(date).locale('en').format('ddd, MMM DD YYYY')
    },
    buildPayload(status) {
      return {
        title: this.form.title,
        content: this.form.description,
        date: moment(this.form.date).format('YYYY-MM-DD'),
        status,
      }
    },
    async submitLog({
      method, url, status, successToast, errorToast,
    }) {
      this.isLoading = true
      try {
        await openApiAxiosIns[method](url, method === 'delete' ? { data: { ids: [Number(this.$route.params.id)] } } : this.buildPayload(status))
        this.$toast_success(successToast)
        this.$router.push(LIST_ROUTE)
      } catch {
        this.$toast_error(errorToast)
      } finally {
        this.isLoading = false
      }
    },
    async getDetail() {
      this.isLoading = true
      try {
        const res = await openApiAxiosIns.get(this.detailUrl)
        const { data } = res.data
        this.form.title = data.title
        this.form.description = data.content
        this.form.date = data.date
        this.status = data.status
      } catch {
        this.$toast_error({ title: 'Update Log', message: 'Update Log gagal diambil' })
      } finally {
        this.isLoading = false
      }
    },
    handleDelete() {
      const isEdit = this.isEditMode
      const swalTitle = isEdit
        ? 'Kamu yakin ingin menghapus update log ini?'
        : 'Apakah kamu yakin ingin membatalkan pembuatan update log?'

      this.$swal({
        title: `<span class="font-medium text-[16px]">${swalTitle}</span>`,
        // eslint-disable-next-line global-require
        imageUrl: require('@/assets/images/icons/warning.svg'),
        confirmButtonText: isEdit ? 'Hapus' : 'Ya',
        confirmButtonClass: 'btn btn-primary rounded-lg',
        showCancelButton: true,
        cancelButtonText: isEdit ? 'Batal' : 'Kembali',
        cancelButtonClass: 'btn btn-outline-primary bg-white text-primary rounded-lg',
      }).then(res => {
        if (!res.isConfirmed) return
        if (isEdit) {
          this.submitLog({
            method: 'delete',
            url: BASE_URL,
            successToast: { title: 'Update Log', message: 'Update Log berhasil dihapus' },
            errorToast: { title: 'Update Log', message: 'Update Log gagal dihapus' },
          })
        } else {
          this.$router.push(LIST_ROUTE)
        }
      })
    },
    handleDraft() {
      this.submitLog({
        method: this.isEditMode ? 'put' : 'post',
        url: this.isEditMode ? this.detailUrl : BASE_URL,
        status: 'draft',
        successToast: { title: 'Update Log', message: 'Update Log berhasil disimpan ke draft' },
        errorToast: { title: 'Update Log', message: 'Update Log gagal dibuat' },
      })
    },
    showConfirmModal() {
      this.$bvModal.show('modal-confirm-update-log')
    },
    async handleConfirm() {
      this.isSubmitting = true
      try {
        if (this.isEditMode) {
          await this.submitLog({
            method: 'put',
            url: this.detailUrl,
            status: 'published',
            successToast: this.isDraft
              ? { title: 'Publish Berhasil', message: 'Log baru kamu berhasil dipublish' }
              : { title: 'Update Berhasil', message: 'Perubahan berhasil disimpan' },
            errorToast: this.isDraft
              ? { title: 'Publish Gagal', message: 'Gagal update log' }
              : { title: 'Update Gagal', message: 'Gagal update log' },
          })
        } else {
          await this.submitLog({
            method: 'post',
            url: BASE_URL,
            status: 'published',
            successToast: { title: 'Publish Berhasil', message: 'Log baru kamu berhasil dipublish' },
            errorToast: { title: 'Publish Gagal', message: 'Gagal update log' },
          })
        }
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>

<style lang="scss">
@import '~@core/scss/vue/libs/vue-flatpicker.scss';
@import '~quill/dist/quill.snow.css';
</style>

<style scoped>
.back-btn {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e8e8e8;
  border-radius: 16px;
  background: #fff;
  cursor: pointer;
  color: #333;
  margin-right: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.15s ease;
}
.back-btn:hover {
  background: #f5f5f5;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.draft-btn {
  background-color: #F0F0F0 !important;
  border: none !important;
  border-radius: 10px !important;
  color: #333 !important;
  font-size: 16px;
  font-weight: 500;
  padding: 10px 28px !important;
}
.draft-btn:hover:not(:disabled) {
  background-color: #e4e4e4 !important;
}
.draft-btn:disabled {
  opacity: 0.5;
}
</style>
